import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getConnections, 
  createConnection, 
  deleteConnection, 
  updateGoogleReviewLink,
  getUserSettings,
  updateResendSettings,
  updateStripeSettings,
  updateShopifySettings,
  changePassword
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Webhook,
  Copy,
  Check,
  CreditCard,
  ShoppingBag,
  Star,
  Mail,
  Eye,
  EyeOff,
  Save,
  Lock
} from 'lucide-react';

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleLink, setGoogleLink] = useState(user?.google_review_link || '');
  const [savingLink, setSavingLink] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(null);
  
  // Settings state
  const [userSettings, setUserSettings] = useState(null);
  const [resendApiKey, setResendApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [savingResend, setSavingResend] = useState(false);
  
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);
  
  const [shopifyApiKey, setShopifyApiKey] = useState('');
  const [shopifyApiSecret, setShopifyApiSecret] = useState('');
  const [shopifyWebhookSecret, setShopifyWebhookSecret] = useState('');
  const [savingShopify, setSavingShopify] = useState(false);
  
  // Show/hide password fields
  const [showResendKey, setShowResendKey] = useState(false);
  const [showStripePublishable, setShowStripePublishable] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showStripeWebhook, setShowStripeWebhook] = useState(false);
  const [showShopifyKey, setShowShopifyKey] = useState(false);
  const [showShopifySecret, setShowShopifySecret] = useState(false);
  const [showShopifyWebhook, setShowShopifyWebhook] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [platform, setPlatform] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [savingConnection, setSavingConnection] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [connectionsData, settingsData] = await Promise.all([
        getConnections(),
        getUserSettings()
      ]);
      setConnections(connectionsData);
      setUserSettings(settingsData);
      setSenderEmail(settingsData.sender_email || '');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoogleLink = async () => {
    if (!googleLink.trim()) {
      toast.error('Please enter a valid Google Review link');
      return;
    }
    setSavingLink(true);
    try {
      await updateGoogleReviewLink(googleLink);
      updateUser({ google_review_link: googleLink });
      toast.success('Google Review link saved');
    } catch (error) {
      toast.error('Failed to save link');
    } finally {
      setSavingLink(false);
    }
  };

  const handleSaveResendSettings = async () => {
    setSavingResend(true);
    try {
      await updateResendSettings({
        resend_api_key: resendApiKey || null,
        sender_email: senderEmail || null
      });
      toast.success('Resend settings saved');
      setResendApiKey('');
      loadData();
    } catch (error) {
      toast.error('Failed to save Resend settings');
    } finally {
      setSavingResend(false);
    }
  };

  const handleSaveStripeSettings = async () => {
    setSavingStripe(true);
    try {
      await updateStripeSettings({
        stripe_publishable_key: stripePublishableKey || null,
        stripe_secret_key: stripeSecretKey || null,
        stripe_webhook_secret: stripeWebhookSecret || null
      });
      toast.success('Stripe settings saved');
      setStripePublishableKey('');
      setStripeSecretKey('');
      setStripeWebhookSecret('');
      loadData();
    } catch (error) {
      toast.error('Failed to save Stripe settings');
    } finally {
      setSavingStripe(false);
    }
  };

  const handleSaveShopifySettings = async () => {
    setSavingShopify(true);
    try {
      await updateShopifySettings({
        shopify_api_key: shopifyApiKey || null,
        shopify_api_secret: shopifyApiSecret || null,
        shopify_webhook_secret: shopifyWebhookSecret || null
      });
      toast.success('Shopify settings saved');
      setShopifyApiKey('');
      setShopifyApiSecret('');
      setShopifyWebhookSecret('');
      loadData();
    } catch (error) {
      toast.error('Failed to save Shopify settings');
    } finally {
      setSavingShopify(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const openConnectionModal = (platformType) => {
    setPlatform(platformType);
    setWebhookSecret('');
    setStoreUrl('');
    setShowModal(true);
  };

  const handleCreateConnection = async (e) => {
    e.preventDefault();
    setSavingConnection(true);
    try {
      await createConnection({
        platform,
        webhook_secret: webhookSecret,
        store_url: storeUrl || null
      });
      toast.success(`${platform} connected successfully`);
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create connection');
    } finally {
      setSavingConnection(false);
    }
  };

  const handleDeleteConnection = async (connectionId, platformName) => {
    if (!window.confirm(`Disconnect ${platformName}?`)) return;
    try {
      await deleteConnection(connectionId);
      toast.success(`${platformName} disconnected`);
      loadData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const copyWebhookUrl = (platform) => {
    const url = `${backendUrl}/api/webhook/${platform}/${user?.id}`;
    navigator.clipboard.writeText(url);
    setCopiedWebhook(platform);
    toast.success('Webhook URL copied to clipboard');
    setTimeout(() => setCopiedWebhook(null), 2000);
  };

  const getWebhookUrl = (platform) => {
    return `${backendUrl}/api/webhook/${platform}/${user?.id}`;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-48 bg-muted rounded-xl" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure your integrations and review settings
        </p>
      </div>

      {/* Google Review Link */}
      <Card className="animate-fade-in" data-testid="google-review-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Google Review Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Enter the link where customers will be directed to leave a review. You can get this from your Google Business Profile.
          </p>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={googleLink}
                onChange={(e) => setGoogleLink(e.target.value)}
                placeholder="https://g.page/r/YOUR_REVIEW_LINK"
                className="font-mono text-sm"
                data-testid="google-link-input"
              />
            </div>
            <Button 
              onClick={handleSaveGoogleLink} 
              disabled={savingLink}
              className="btn-animate"
              data-testid="save-google-link-btn"
            >
              {savingLink ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resend Email Settings */}
      <Card className="animate-fade-in" data-testid="resend-settings-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            Resend Email Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Configure your Resend account to send review request emails. Get your API key from{' '}
            <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              resend.com/api-keys
            </a>
          </p>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label htmlFor="resendApiKey">API Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="resendApiKey"
                    type={showResendKey ? "text" : "password"}
                    value={resendApiKey}
                    onChange={(e) => setResendApiKey(e.target.value)}
                    placeholder={userSettings?.resend_api_key_set ? `Current: ${userSettings.resend_api_key_masked}` : "re_xxxxxxxxxxxxx"}
                    className="font-mono text-sm pr-10"
                    data-testid="resend-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResendKey(!showResendKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.resend_api_key_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> API key configured
                </p>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="senderEmail">Sender Email Domain</Label>
              <Input
                id="senderEmail"
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="reviews@yourdomain.com"
                className="mt-1"
                data-testid="sender-email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Must be a verified domain in your Resend account
              </p>
            </div>

            <Button 
              onClick={handleSaveResendSettings} 
              disabled={savingResend}
              className="btn-animate"
              data-testid="save-resend-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingResend ? 'Saving...' : 'Save Resend Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Settings */}
      <Card className="animate-fade-in" data-testid="stripe-settings-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-500" />
            Stripe Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Configure your Stripe account to automatically capture customers after payments. Get your keys from{' '}
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Stripe Dashboard
            </a>
          </p>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label htmlFor="stripePublishableKey">Publishable Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="stripePublishableKey"
                    type={showStripePublishable ? "text" : "password"}
                    value={stripePublishableKey}
                    onChange={(e) => setStripePublishableKey(e.target.value)}
                    placeholder={userSettings?.stripe_publishable_key_set ? `Current: ${userSettings.stripe_publishable_key_masked}` : "pk_live_xxxxxxxxxxxxx"}
                    className="font-mono text-sm pr-10"
                    data-testid="stripe-publishable-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripePublishable(!showStripePublishable)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showStripePublishable ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.stripe_publishable_key_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Publishable key configured
                </p>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="stripeSecretKey">Secret Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="stripeSecretKey"
                    type={showStripeSecret ? "text" : "password"}
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    placeholder={userSettings?.stripe_secret_key_set ? `Current: ${userSettings.stripe_secret_key_masked}` : "sk_live_xxxxxxxxxxxxx"}
                    className="font-mono text-sm pr-10"
                    data-testid="stripe-secret-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeSecret(!showStripeSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showStripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.stripe_secret_key_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Secret key configured
                </p>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="stripeWebhookSecret">Webhook Secret</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="stripeWebhookSecret"
                    type={showStripeWebhook ? "text" : "password"}
                    value={stripeWebhookSecret}
                    onChange={(e) => setStripeWebhookSecret(e.target.value)}
                    placeholder={userSettings?.stripe_webhook_secret_set ? `Current: ${userSettings.stripe_webhook_secret_masked}` : "whsec_xxxxxxxxxxxxx"}
                    className="font-mono text-sm pr-10"
                    data-testid="stripe-webhook-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeWebhook(!showStripeWebhook)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showStripeWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.stripe_webhook_secret_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Webhook secret configured
                </p>
              )}
            </div>

            <Button 
              onClick={handleSaveStripeSettings} 
              disabled={savingStripe}
              className="btn-animate"
              data-testid="save-stripe-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingStripe ? 'Saving...' : 'Save Stripe Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Shopify Settings */}
      <Card className="animate-fade-in" data-testid="shopify-settings-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-green-500" />
            Shopify Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Configure your Shopify API credentials to capture customers after orders. Get your keys from{' '}
            <a href="https://admin.shopify.com/store/YOUR_STORE/settings/apps/development" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Shopify Admin → Settings → Apps → Develop apps
            </a>
          </p>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label htmlFor="shopifyApiKey">API Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="shopifyApiKey"
                    type={showShopifyKey ? "text" : "password"}
                    value={shopifyApiKey}
                    onChange={(e) => setShopifyApiKey(e.target.value)}
                    placeholder={userSettings?.shopify_api_key_set ? `Current: ${userSettings.shopify_api_key_masked}` : "Your Shopify API key"}
                    className="font-mono text-sm pr-10"
                    data-testid="shopify-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowShopifyKey(!showShopifyKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showShopifyKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.shopify_api_key_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> API key configured
                </p>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="shopifyApiSecret">API Secret Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="shopifyApiSecret"
                    type={showShopifySecret ? "text" : "password"}
                    value={shopifyApiSecret}
                    onChange={(e) => setShopifyApiSecret(e.target.value)}
                    placeholder={userSettings?.shopify_api_secret_set ? `Current: ${userSettings.shopify_api_secret_masked}` : "shpss_xxxxxxxxxxxxx"}
                    className="font-mono text-sm pr-10"
                    data-testid="shopify-api-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowShopifySecret(!showShopifySecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showShopifySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.shopify_api_secret_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> API secret configured
                </p>
              )}
            </div>

            <div className="form-group">
              <Label htmlFor="shopifyWebhookSecret">Webhook Signing Secret</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    id="shopifyWebhookSecret"
                    type={showShopifyWebhook ? "text" : "password"}
                    value={shopifyWebhookSecret}
                    onChange={(e) => setShopifyWebhookSecret(e.target.value)}
                    placeholder={userSettings?.shopify_webhook_secret_set ? `Current: ${userSettings.shopify_webhook_secret_masked}` : "Your webhook signing secret"}
                    className="font-mono text-sm pr-10"
                    data-testid="shopify-webhook-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowShopifyWebhook(!showShopifyWebhook)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showShopifyWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {userSettings?.shopify_webhook_secret_set && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Webhook secret configured
                </p>
              )}
            </div>

            <Button 
              onClick={handleSaveShopifySettings} 
              disabled={savingShopify}
              className="btn-animate"
              data-testid="save-shopify-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingShopify ? 'Saving...' : 'Save Shopify Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Webhook URLs */}
      <Card className="animate-fade-in" data-testid="webhooks-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Webhook className="w-5 h-5 text-primary" />
            Webhook URLs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Copy these webhook URLs to your Stripe and Shopify dashboards to automatically capture customers after purchases.
          </p>

          {/* Stripe Webhook */}
          <div className="border border-border rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Stripe Webhook</h3>
                <p className="text-xs text-muted-foreground">For <code className="bg-muted px-1 rounded">checkout.session.completed</code> events</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={getWebhookUrl('stripe')}
                readOnly
                className="font-mono text-xs bg-muted/50"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyWebhookUrl('stripe')}
                data-testid="copy-stripe-webhook"
              >
                {copiedWebhook === 'stripe' ? (
                  <Check className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Shopify Webhook */}
          <div className="border border-border rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Shopify Webhook</h3>
                <p className="text-xs text-muted-foreground">For <code className="bg-muted px-1 rounded">orders/create</code> events</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={getWebhookUrl('shopify')}
                readOnly
                className="font-mono text-xs bg-muted/50"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyWebhookUrl('shopify')}
                data-testid="copy-shopify-webhook"
              >
                {copiedWebhook === 'shopify' ? (
                  <Check className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="animate-fade-in" data-testid="change-password-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-500" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Update your account password. You'll need to enter your current password for verification.
          </p>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min. 6 characters)"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type={showNewPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              onClick={handleChangePassword} 
              disabled={savingPassword}
              className="btn-animate"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {savingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Connection Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">Connect {platform}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateConnection} className="space-y-5">
            <div className="form-group">
              <Label htmlFor="webhookSecret">Webhook Secret (Optional)</Label>
              <Input
                id="webhookSecret"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={platform === 'stripe' ? 'whsec_...' : 'Your webhook secret'}
                data-testid="webhook-secret-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used to verify webhook signatures for security
              </p>
            </div>

            {platform === 'shopify' && (
              <div className="form-group">
                <Label htmlFor="storeUrl">Store URL (Optional)</Label>
                <Input
                  id="storeUrl"
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value)}
                  placeholder="yourstore.myshopify.com"
                  data-testid="store-url-input"
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingConnection} data-testid="save-connection-btn">
                {savingConnection ? 'Connecting...' : 'Connect'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
